import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { CheckCircle2, CircleAlert, Info, X } from "lucide-react";

const NotificationContext = createContext(null);

const iconByType = {
  success: CheckCircle2,
  error: CircleAlert,
  info: Info,
};

const colorByType = {
  success: "border-emerald-200 bg-emerald-50 text-emerald-800",
  error: "border-rose-200 bg-rose-50 text-rose-800",
  info: "border-blue-200 bg-blue-50 text-blue-800",
};

export const NotificationProvider = ({ children }) => {
  const [items, setItems] = useState([]);

  const remove = useCallback((id) => {
    setItems((prev) => prev.filter((x) => x.id !== id));
  }, []);

  const notify = useCallback((payload) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    const type = payload?.type || "info";
    const next = {
      id,
      title: payload?.title || "Notification",
      message: payload?.message || "",
      type,
    };
    setItems((prev) => [...prev, next]);
    window.setTimeout(() => remove(id), 3500);
  }, [remove]);

  const value = useMemo(() => ({ notify }), [notify]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <div className="fixed z-[9999] top-4 right-4 space-y-3 w-[92vw] max-w-sm">
        {items.map((item) => {
          const Icon = iconByType[item.type] || Info;
          return (
            <div key={item.id} className={`rounded-xl border shadow-lg p-3 ${colorByType[item.type] || colorByType.info}`}>
              <div className="flex items-start gap-3">
                <Icon size={18} className="mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="font-semibold text-sm">{item.title}</p>
                  {item.message ? <p className="text-sm mt-0.5">{item.message}</p> : null}
                </div>
                <button
                  type="button"
                  onClick={() => remove(item.id)}
                  className="opacity-70 hover:opacity-100"
                  aria-label="Dismiss notification"
                >
                  <X size={16} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </NotificationContext.Provider>
  );
};

export const useNotification = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error("useNotification must be used inside NotificationProvider");
  return ctx;
};
